import { useState } from 'react';
import { addSettlement } from '../lib/db';
import { track } from '../lib/analytics';
import { curOf, money, todayISO, toMinor } from '../lib/money';
import { Note, Sheet } from '../components/ui';
import type { TripData } from '../types';
import type { SettleTarget } from './Trip';

export default function Settle({ data, uid, target, onClose, onSaved }: {
  data: TripData;
  uid: string;
  target: SettleTarget;
  onClose: () => void;
  onSaved: (label: string) => void;
}) {
  const { group, units } = data;
  const symbol = curOf(group.currency).symbol;
  const defaultMethod = group.currency === 'INR' ? 'UPI' : 'Venmo or Zelle';

  const [from, setFrom] = useState(target.fromUnitId);
  const [to, setTo] = useState(target.toUnitId);
  const [amount, setAmount] = useState((target.amountMinor / 100).toFixed(2));
  const [method, setMethod] = useState(defaultMethod);
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountMinor = toMinor(amount);
  const canSave = amountMinor > 0 && from !== to && !saving;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await addSettlement(group.id, uid, {
        fromUnitId: from, toUnitId: to, amountMinor, date, method: method.trim()
      });
      track('payment_recorded');
      onSaved(`Payment of ${money(amountMinor, group.currency)} recorded`);
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Sheet
      title="Record a payment"
      onClose={onClose}
      footer={
        <button className="btn" disabled={!canSave} onClick={() => void save()}>
          {saving ? 'Recording…' : 'Record payment'}
        </button>
      }
    >
      <div className="amount-field">
        <span>{symbol}</span>
        <input id="s-amt" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="s-from">Paid by</label>
        <select id="s-from" className="inp" value={from} onChange={(e) => setFrom(e.target.value)}>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="s-to">Paid to</label>
        <select id="s-to" className="inp" value={to} onChange={(e) => setTo(e.target.value)}>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {from === to && (
        <p style={{ color: 'var(--clay)', fontSize: 12.5, fontWeight: 600, margin: '8px 2px 0' }}>
          Pick two different families.
        </p>
      )}

      <div className="field">
        <label htmlFor="s-method">Method</label>
        <input id="s-method" className="inp" value={method} autoComplete="off"
          onChange={(e) => setMethod(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="s-date">Date</label>
        <input id="s-date" className="inp" type="date" value={date}
          onChange={(e) => setDate(e.target.value)} />
      </div>

      {error && <div style={{ marginTop: 14 }}><Note>Couldn't record that: {error}</Note></div>}

      <div style={{ marginTop: 16 }}>
        <Note>
          This only records that money changed hands — Evenhouse doesn't move it.
          Pay by {defaultMethod} as usual, then log it here.
        </Note>
      </div>
    </Sheet>
  );
}
